/** Formatting helpers. Nothing here talks to the network. */

const NUM = new Intl.NumberFormat('en-US');

export function formatNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? NUM.format(value) : '-';
}

/**
 * Seconds → the two most significant units: "23h 43m", "6d 4h", "12m 09s".
 * Ops readers want the magnitude, not the precision.
 */
export function formatUptime(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '-';

  const total = Math.floor(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(secs).padStart(2, '0')}s`;
  return `${secs}s`;
}

export function formatMb(mb) {
  if (typeof mb !== 'number' || !Number.isFinite(mb)) return '-';
  return `${NUM.format(Math.round(mb))} MB`;
}

export function formatGb(gb) {
  if (typeof gb !== 'number' || !Number.isFinite(gb)) return '-';
  return `${NUM.format(Math.round(gb))} GB`;
}

export function percent(used, total) {
  if (!total || typeof used !== 'number' || !Number.isFinite(used)) return null;
  return Math.max(0, Math.min(100, (used / total) * 100));
}

export function formatPercent(value, digits = 0) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return `${value.toFixed(digits)}%`;
}

export function formatLoad(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '-';
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "1 Aug 2026, 19:55:12" in the viewer's locale-neutral 24h form. */
export function formatTimestamp(value) {
  const date = parseDate(value);
  if (!date) return typeof value === 'string' && value ? value : '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatRelative(value, now = Date.now()) {
  const date = parseDate(value);
  if (!date) return '';

  const deltaSeconds = Math.round((date.getTime() - now) / 1000);
  const magnitude = Math.abs(deltaSeconds);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (magnitude < 60) return rtf.format(Math.round(deltaSeconds), 'second');
  if (magnitude < 3600) return rtf.format(Math.round(deltaSeconds / 60), 'minute');
  if (magnitude < 86400) return rtf.format(Math.round(deltaSeconds / 3600), 'hour');
  return rtf.format(Math.round(deltaSeconds / 86400), 'day');
}

export function formatClock(ms) {
  if (!ms) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(ms));
}

/**
 * "431072-496607" → 65536. The subuid range width is the isolation story, so
 * we show it rather than making the reader subtract.
 */
export function subuidWidth(range) {
  if (typeof range !== 'string') return null;
  const match = range.match(/^\s*(\d+)\s*-\s*(\d+)\s*$/);
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2]);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return end - start + 1;
}

export function countLines(text) {
  if (typeof text !== 'string' || text.length === 0) return 0;
  return text.replace(/\n+$/, '').split('\n').length;
}
